import type { Components, Theme } from "@mui/material/styles";

import { color, effect, radius, spacing } from "@app/theme/weaveTokens.ts";
import { typographyVariant } from "@app/theme/typography.ts";

type ThemeComponents = Components<Omit<Theme, "components">>;

const toolbarSize = {
  padding: `${spacing.xs} ${spacing.m}`,
  minHeight: `calc(${spacing.xxl} + ${spacing.xxs})`,
};

/**
 * Restores the Digital HIG Toolbar: the black rounded bar with white text that
 * the apps bulk action toolbar is built on. Weave's legacy theme has no
 * `MuiToolbar` entry, so without this the bar had no background, radius or
 * shadow and its (white-on-black) buttons floated over the table rows.
 *
 * MUI's Toolbar sets its own padding and `minHeight` at the `sm` breakpoint
 * and in landscape, so DHIG restates the same values there.
 */
export const toolbarOverrides: ThemeComponents = {
  MuiToolbar: {
    styleOverrides: {
      root: {
        boxShadow: effect.dropdown,
        ...typographyVariant["short-copy-medium"],
        backgroundColor: color.fillPrimary,
        borderRadius: radius.large,
        color: color.inkOnFill,
        boxSizing: "border-box",
        justifyContent: "space-between",
        ...toolbarSize,
        '& [class*="MuiIconButton-root"]': {
          marginRight: spacing.xs,
        },
        '& button[class*="MuiButton-root"]': {
          backgroundColor: color.fillPrimary,
          borderColor: color.inkOnFill,
          color: color.inkOnFill,
          whiteSpace: "nowrap",
        },
        '& button[class*="MuiButton-root"]:focus:active': {
          color: color.inkOnFill,
        },
        '& [class*="MuiTypography-root"]': {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          marginRight: spacing.m,
        },
        "@media (min-width: 600px)": toolbarSize,
        "@media (min-width: 0px) and (orientation: landscape)": {
          minHeight: toolbarSize.minHeight,
        },
      },
    },
  },
};
